// What ticking a board costs, in credits, before anybody ticks it.
//
// WHO ACTUALLY PAYS, because the answer is not "the app" and it is not one
// pool. There are two keys in this system and they buy different things:
//
//   THE PUNTER'S KEY buys the BOARD. It is pasted in Settings, stored on the
//   device, and sent per request to /api/odds, which refuses outright without
//   one (`odds-key-required`). One credit per sport per fetch — the fetch asks
//   for `h2h` in the `au` region, and The Odds API charges markets × regions,
//   which is 1 × 1. The day is fetched once, so a sport costs its owner about
//   one credit a day, plus one more each time they fetch the prices again on
//   purpose.
//
//   THE SERVER'S KEY buys RESULTS. `ODDS_API_KEY` on Cloud Run, used by the two
//   scheduled jobs in server/cron/ — closing prices and settlement. A scores
//   call costs 2 credits per sport, and none of them come off the punter's
//   plan. So ticking more boards costs more to PRICE and nothing more to
//   SETTLE, which is the opposite of what you would guess and worth saying out
//   loud on the screen that spends it.
//
// The free plan is 500 credits a month — about 16 a day — so this arithmetic
// is comfortable for a dozen boards and worth printing for forty.
//
// It lives here, as pure functions over plain numbers, rather than inline in
// the picker: the picker prints these figures in three places (a live estimate,
// a confirm dialog and an over-plan warning) and three copies of a sum is three
// chances for one of them to be wrong in a way nobody notices.

/** One sport, one fetch: h2h × au = 1 × 1. See the header. */
export const CREDITS_PER_SPORT = 1;

/** The board is fetched once a day. A manual refresh is an extra one of these. */
export const FETCHES_PER_DAY = 1;

/** The Odds API free tier. */
export const FREE_PLAN = 500;

/**
 * Days in a "month" for the purposes of a projection.
 *
 * Thirty, flat, and deliberately not the length of the current calendar month:
 * the plan's own reset is the anniversary of the signup, not the first of the
 * month, so any arithmetic pretending to know the exact window would be precise
 * about something it cannot see. Thirty is honest to the nearest day and reads
 * as the round number it is.
 */
export const DAYS_PER_MONTH = 30;

/** A count of boards, from a number or from the list itself. Never negative. */
function count(sports) {
  const n = Array.isArray(sports) ? sports.length : Number(sports);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * What a set of boards costs: `{ sports, perFetch, perDay, perMonth,
 * percentOfPlan, overPlan }`.
 *
 * `sports` may be the list or its length, because both callers have one of each
 * to hand. `percentOfPlan` is rounded to a whole number — a projection accurate
 * to a tenth of a per cent would be claiming a precision the month does not
 * have.
 */
export function estimateCredits(sports, {
  plan = FREE_PLAN,
  days = DAYS_PER_MONTH,
  fetchesPerDay = FETCHES_PER_DAY,
} = {}) {
  const n = count(sports);
  const perFetch = n * CREDITS_PER_SPORT;
  const perDay = perFetch * fetchesPerDay;
  const perMonth = perDay * days;
  return {
    sports: n,
    perFetch,
    perDay,
    perMonth,
    percentOfPlan: plan > 0 ? Math.round((perMonth / plan) * 100) : null,
    overPlan: plan > 0 && perMonth > plan,
  };
}

// --- the one sentence, per provider -------------------------------------------
// Everything above this line is the board picker's arithmetic, and it is The
// Odds API's: one credit per sport, 500 a month. The sentence below is printed
// in three more places — the card somebody reads before they have a key, the
// Providers section, and the foot of the picker — and it has to be right for
// whichever provider is actually buying the board, because a punter on OddsPapi
// reading a figure worked out for The Odds API is being told a number that is
// not theirs.
//
// The costs are the adapters' own `cost.odds`, copied. That duplication is the
// same one `lib/providers.js` already documents and for the same reason: this
// line prints on a first-run phone with no key, which has nothing to ask the
// server WITH. Keep them in step with server/providers/*.mjs.

/**
 * The estimate assumes TWO fetches a day, not one.
 *
 * The board drops once and that is the only fetch anybody is charged for
 * automatically — but a punter who is about to hand over a key wants the number
 * they will actually see, and in practice a morning's board gets looked at again
 * before the first kick-off. Estimating low is the one direction this sentence
 * must not be wrong in.
 */
export const FETCHES_ESTIMATED = 2;

/** The provider the board comes from unless something says otherwise. */
const BOARD_PROVIDER = 'the-odds-api';

/** The free PuntersEdge plan. Declared here because PROVIDER_PLANS below reads it. */
export const RACING_FREE_PLAN = 1500;

/**
 * What one fetch of `n` boards costs each provider, and what its free tier
 * carries in a month. `unit` is the provider's own word for the thing it meters
 * — credits, requests, objects — because "credits" on a key that counts objects
 * is a figure a punter cannot check against their own dashboard.
 *
 *   the-odds-api     markets × regions, and the fetch asks for h2h in au: 1 × 1.
 *   oddspapi         one request for the sports list, one per sport for the
 *                    fixtures, then one per fixture — capped at MAX_FIXTURES.
 *   sportsgameodds   billed per event returned, a page at a time, plus the one
 *                    object reading the meter costs.
 *   betfair-exchange no credits at all (and no free tier to measure against).
 *   squiggle         free, and it needs no key.
 *   puntersedge      NOTHING PER SPORT, and the zero is not a placeholder: a
 *                    racing card is one call for the whole day's racing,
 *                    whatever is ticked on the sports picker, so the arithmetic
 *                    above simply does not describe this provider. What it
 *                    costs is `racingCreditLine` below.
 */
export const PROVIDER_PLANS = {
  'the-odds-api': { cost: (n) => n, freeMonthly: FREE_PLAN, unit: 'credit' },
  oddspapi: { cost: (n) => (n ? 1 + n + 40 : 0), freeMonthly: 250, unit: 'request' },
  sportsgameodds: { cost: (n) => (n ? Math.min(n * 50, 120) + 1 : 0), freeMonthly: 2500, unit: 'object' },
  'betfair-exchange': { cost: () => 0, freeMonthly: null, unit: 'credit' },
  squiggle: { cost: () => 0, freeMonthly: null, unit: 'credit' },
  puntersedge: { cost: () => 0, freeMonthly: RACING_FREE_PLAN, unit: 'credit' },
};

// --- what racing costs ---------------------------------------------------------
//
// A different shape of sum from everything above, because racing is not billed
// per board. One `next-to-go` call is the whole day's card across all three
// codes, and the budget is a count of CALLS rather than a count of sports:
//
//   next-to-go     2 cr  x  up to 6 pulls a day   = 12
//   movers         3 cr  x  up to 3               =  9
//   results        2 cr  x  up to 2, and only with a racing leg pending
//                                                 =  4
//   price-history  5 cr  x  up to 4 placed races  = 20
//                                                   --
//                                             about 45, call it 40 on a
//                                             day nobody places four bets.
//
// THE SIX PULLS ARE AUTOMATIC NOW and the figure is unchanged, which is the
// whole point of capping them at six: from 2026-09-16 the card follows the
// board — it is bought at boot, on a resume and on an enter of Today, wherever
// it is over an hour old — rather than waiting for somebody to open the Racing
// tab. `lib/racing/auto.js` is that rule and it is the same six calls a busy
// day always made, spent when the prices actually move instead of when a punter
// happens to go looking.
//
// NOTHING POLLS. Every one of those rides an open, a resume, a tap or a settle,
// and there is no timer anywhere in the app. The figure is printed under the
// PuntersEdge card in Settings so the reader can hold it against their own
// dashboard.

/** A day of racing on the free plan, in credits. See the sum above. */
export const RACING_CREDITS_PER_DAY = 40;

/** What the automatic half of that is: the card alone, and how many of it. */
export const RACING_CARD_CREDITS = 2;
export const RACING_AUTO_PULLS = 6;

/** Small numbers read as words in a sentence; "up to 6 times a day" does not. */
const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const word = (n) => WORDS[n] ?? n.toLocaleString();

/**
 * THE CADENCE AND THE CAP, IN THE PUNTER'S OWN TERMS:
 *
 *   "Legwork pulls the racing card when the app opens and it is over an hour
 *    old, up to six times a day: about 12 credits a day, 360 a month of the
 *    free 1,500. Refresh and movers are extra."
 *
 * The app spending a punter's credits without being asked is a thing it has to
 * say out loud, in the two places they would look for it: the PuntersEdge card
 * in Settings › Providers, and the racing switches on the same screen. Both
 * print this one sentence, so neither can drift from the other or from the
 * constants above.
 */
export function racingAutoLine({
  pulls = RACING_AUTO_PULLS,
  perPull = RACING_CARD_CREDITS,
  days = DAYS_PER_MONTH,
  freeMonthly = RACING_FREE_PLAN,
} = {}) {
  const perDay = pulls * perPull;
  return `Legwork pulls the racing card when the app opens and it is over an hour old, up to `
    + `${word(pulls)} times a day: about ${perDay.toLocaleString()} credits a day, `
    + `${(perDay * days).toLocaleString()} a month of the free ${freeMonthly.toLocaleString()}. `
    + 'Refresh and movers are extra.';
}

/**
 * "Racing costs about 40 credits a day — 1,200 a month on the 1,500-credit free key."
 *
 * One sentence, one shape, and the same tail `creditLine` uses, so the two read
 * as the same voice on the same screen.
 */
export function racingCreditLine({
  perDay = RACING_CREDITS_PER_DAY,
  days = DAYS_PER_MONTH,
  freeMonthly = RACING_FREE_PLAN,
} = {}) {
  const perMonth = perDay * days;
  // "the" rather than "a": there is one PuntersEdge free key and this is its
  // size, where `creditLine` above is talking about whichever provider a reader
  // happens to be on.
  const tail = freeMonthly
    ? `${perMonth.toLocaleString()} a month on the ${freeMonthly.toLocaleString()}-credit free key`
    : `${perMonth.toLocaleString()} a month`;
  return `Racing costs about ${perDay.toLocaleString()} credits a day, ${tail}.`;
}

const planFor = (provider) => PROVIDER_PLANS[provider] ?? PROVIDER_PLANS[BOARD_PROVIDER];

/**
 * What a punter's boards cost them: `{ sports, perFetch, perDay, perMonth,
 * freeMonthly, unit }`.
 *
 * `cost` overrides the provider's own function — handed in by a caller that has
 * the adapter to hand, and by the tests. `sports` takes the list or its length,
 * as everything else in this file does.
 */
export function creditEstimate({
  sports,
  fetchesPerDay = FETCHES_ESTIMATED,
  provider = BOARD_PROVIDER,
  cost = null,
  freeMonthly = undefined,
  days = DAYS_PER_MONTH,
} = {}) {
  const p = planFor(provider);
  const n = count(sports);
  const perFetch = Math.max(0, Math.round((cost ?? p.cost)(n)));
  const perDay = perFetch * Math.max(0, fetchesPerDay);
  return {
    sports: n,
    perFetch,
    perDay,
    perMonth: perDay * days,
    freeMonthly: freeMonthly === undefined ? p.freeMonthly : freeMonthly,
    unit: p.unit,
  };
}

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

/**
 * The sentence, in one shape, wherever it prints:
 *
 *   "Your 4 sports cost about 8 credits a day — 240 a month on a 500-credit
 *    free key."
 *
 * The free-key tail is dropped when the provider has no free tier to name
 * rather than guessed at, and a board of nothing says so plainly instead of
 * printing a row of zeroes.
 */
export function creditLine(sports, options = {}) {
  const e = creditEstimate({ sports, ...options });
  if (e.sports === 0) return 'No boards ticked, so nothing to fetch and nothing to pay.';
  const verb = e.sports === 1 ? 'costs' : 'cost';
  const head = `Your ${plural(e.sports, 'sport')} ${verb} about ${plural(e.perDay, e.unit)} a day`;
  const tail = e.freeMonthly
    ? `${e.perMonth.toLocaleString()} a month on a ${e.freeMonthly.toLocaleString()}-${e.unit} free key`
    : `${e.perMonth.toLocaleString()} a month`;
  return `${head}, ${tail}.`;
}

/**
 * How many boards a plan carries for a whole month at one fetch a day.
 *
 * 500 ÷ 30 is 16, which is the number the picker can stand behind: below it the
 * month is covered by the daily fetch alone, above it the daily fetch on its own
 * would run the plan out. It says nothing about manual refreshes, which is why
 * the screen prints the running estimate beside it rather than this figure on
 * its own.
 */
export function sustainableSports({
  plan = FREE_PLAN,
  days = DAYS_PER_MONTH,
  fetchesPerDay = FETCHES_PER_DAY,
} = {}) {
  const perSport = CREDITS_PER_SPORT * fetchesPerDay * days;
  return perSport > 0 ? Math.floor(plan / perSport) : 0;
}

/**
 * Days left in the calendar month, counting today.
 *
 * The calendar month is a stand-in for the plan's own window — see
 * DAYS_PER_MONTH — and it is the only one a device can compute without asking
 * the provider when the account was opened. It is used for one sentence ("this
 * runs out before the month is up"), where being a few days out changes nothing
 * a reader would act on differently.
 */
export function daysLeftInMonth(at = Date.now()) {
  const d = new Date(at);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return lastDay - d.getDate() + 1;
}

/**
 * The estimate, plus what it means for the balance actually on the key:
 * `{ …estimate, remaining, daysCovered, daysLeft, runsOutEarly }`.
 *
 * `remaining` is what the provider last told us was left (store.js keeps it as
 * `oddsCredits.remaining`); null when nothing has been fetched yet, and then
 * `daysCovered` and `runsOutEarly` are null too rather than guessed. An unknown
 * balance must not produce a warning — telling somebody their plan will run out
 * when we have no idea what is on it is the kind of false alarm that teaches
 * people to ignore the true ones.
 */
export function planOutlook({ sports, remaining = null, at = Date.now(), ...options } = {}) {
  const estimate = estimateCredits(sports, options);
  const left = Number.isFinite(Number(remaining)) && remaining !== null ? Number(remaining) : null;
  const daysCovered = left === null || estimate.perDay <= 0
    ? null
    : Math.floor(left / estimate.perDay);
  const daysLeft = daysLeftInMonth(at);
  return {
    ...estimate,
    remaining: left,
    daysCovered,
    daysLeft,
    runsOutEarly: daysCovered !== null && daysCovered < daysLeft,
  };
}
